/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import round from 'lodash/round';
import {
    Button,
    Heading,
    Icon,
    Spinner,
    StarRating,
    StyledModal,
    Text,
} from 'sarsaparilla';
import { useAuthModal } from 'shared-ui/auth';
import { useGetAggregateReviews, useGetAIReviewSummary } from '../api/queries';
import { postHelpfulAISummary, postReportAISummary } from '../api/rating-review-api';
import CellCoverageIcon from '../components/CellCoverageIcon';
import { describeCoverage, describeCoverageLong } from '../utils/cellCoverage';
import type { cellAggregateRating, starCounts } from '../model/aggregate';

import styles from './RatingsReviews.module.scss';

// Roughly six lines of body text on a 375–414px-wide phone (~40–55 chars/line).
// Lines up with the mobile design comp's "Read the Full Review" cutoff.
const SUMMARY_TRUNCATE_LENGTH = 250;
const RR_MOBILE_MAX_PX = 767;

function useElementWidth<T extends HTMLElement>(): [React.RefObject<T>, number | null] {
    const ref = useRef<T>(null) as React.RefObject<T>;
    const [width, setWidth] = useState<number | null>(null);

    useEffect(() => {
        const el = ref.current;

        if (!el || typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(([entry]) => {
            setWidth(entry.contentRect.width);
        });

        observer.observe(el);

        return () => {
            observer.disconnect();
        };
    }, []);

    return [ref, width];
}

type PropTypes = {
    locationId: string;
    locationType: string;
    noHeading?: boolean;
    className?: string;
};

export default function RatingsReviews({
    locationId,
    locationType,
    noHeading = false,
    className,
}: PropTypes) {
    const [wrapperRef, wrapperWidth] = useElementWidth<HTMLDivElement>();
    // `wrapperWidth` is null until the first ResizeObserver tick — fall back
    // to the desktop layout to avoid flashing the mobile branch on paint.
    const isMobile = wrapperWidth !== null && wrapperWidth <= RR_MOBILE_MAX_PX;
    const [isExpanded, setIsExpanded] = useState(false);

    const { isLoading: isAggregateLoading, data: aggregate } = useGetAggregateReviews({
        locationId,
        locationType,
    });
    const { isLoading: isAiReviewLoading, data: aiReview } = useGetAIReviewSummary({
        locationId,
        locationType,
    });

    if (isAggregateLoading || !aggregate || !aggregate.location_id) {
        return null;
    }

    if (aggregate.number_of_ratings <= 0 && !aiReview?.summary) {
        return null;
    }

    // The collapsed "Show Full Review" affordance is Campground-mobile only.
    // Everywhere else (other location types, or desktop) the summary is
    // always fully expanded with the breakdown bars and Helpful/Report visible.
    const isCampgroundMobile = isMobile && locationType === 'Campground';
    const isMobileCollapsed = isCampgroundMobile && !isExpanded;

    return (
        <div
            ref={wrapperRef}
            className={cx(styles.container, className, 'ratings-reviews', {
                [styles.campgroundMobile]: isCampgroundMobile,
                [styles.mobileCollapsed]: isMobileCollapsed,
            })}
        >
            <div className="mb-2">
                <div className={styles.headerRow}>
                    {!noHeading && (
                        <Heading
                            headingLevel={2}
                            appearance="h5"
                            className={styles.heading}
                            hasUnderline={isCampgroundMobile ? 'left' : undefined}
                        >
                            Visitor Ratings & Reviews
                        </Heading>
                    )}
                    {/* Single-star compact rating for mobile (top right of heading) */}
                    <div className={styles.starMobile}>
                        <StarRating
                            rating={aggregate.average_rating}
                            reviewCount={aggregate.number_of_ratings}
                            size="md"
                            isSingleStar
                        />
                    </div>
                </div>
                {/* Single-star + count for desktop, lives directly under the heading */}
                <div className={styles.starDesktop}>
                    <StarRating
                        rating={aggregate.average_rating}
                        reviewCount={aggregate.number_of_ratings}
                        size="md"
                        isSingleStar
                    />
                </div>
            </div>
            <div className={styles.body}>
                <AiSummaryBlock
                    summary={aiReview?.summary}
                    loading={isAiReviewLoading}
                    isCampgroundMobile={isCampgroundMobile}
                    isExpanded={isExpanded}
                    onToggleExpanded={() => setIsExpanded((prev) => !prev)}
                    locationId={locationId}
                    locationType={locationType}
                />
                <ReadOnlyRatingBreakdown
                    total={aggregate.number_of_ratings}
                    starCounts={aggregate.star_counts}
                />
                {isCampgroundMobile && isExpanded && (
                    <Button
                        appearance="tertiary"
                        shouldFitContainer
                        className="mt-2"
                        onClick={() => setIsExpanded(false)}
                    >
                        Hide the Full Review
                    </Button>
                )}
            </div>
            {!!aggregate.aggregate_cell_coverage_ratings?.length && (
                <MobileCoverageRow
                    cellRatings={aggregate.aggregate_cell_coverage_ratings}
                    isMobile={isMobile}
                    isCampgroundMobile={isCampgroundMobile}
                />
            )}
        </div>
    );
}

type AiSummaryBlockProps = {
    summary: string | undefined;
    loading: boolean;
    isCampgroundMobile: boolean;
    isExpanded: boolean;
    onToggleExpanded: () => void;
    locationId: string;
    locationType: string;
};

function AiSummaryBlock({
    summary,
    loading,
    isCampgroundMobile,
    isExpanded,
    onToggleExpanded,
    locationId,
    locationType,
}: AiSummaryBlockProps) {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { ensureLoggedIn } = useAuthModal();

    const handleHelpfulClick = async () => {
        if (await ensureLoggedIn()) {
            setIsSubmitted(true);
            try {
                postHelpfulAISummary({ locationId, locationType });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('Error posting helpful', error);
            }
        }
    };

    const handleReportClick = async () => {
        if (await ensureLoggedIn()) {
            setIsSubmitted(true);
            try {
                postReportAISummary({ locationId, locationType });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('Error posting report', error);
            }
        }
    };

    if (loading && !summary) {
        return (
            <div className={styles.summary}>
                <Spinner size="sm" />
            </div>
        );
    }

    if (!summary) {
        return (
            <div className={styles.noAiSummaryText}>
                <span>No AI summary is available to view at this time.</span>
            </div>
        );
    }

    // Campground+mobile shows a "Read the Full Review" link that reveals
    // the breakdown bars and Helpful/Report below. The truncation only
    // kicks in if the summary itself is long; the link renders either way
    // so the user can always reach the rest of the content.
    const isLong = summary.length > SUMMARY_TRUNCATE_LENGTH;
    const showReadFullReviewLink = isCampgroundMobile && !isExpanded;
    const showTruncated = showReadFullReviewLink && isLong;
    const visibleText = showTruncated
        ? `${summary.slice(0, SUMMARY_TRUNCATE_LENGTH).trimEnd()}…`
        : summary;

    return (
        <div className={styles.summary}>
            <Heading headingLevel={3} appearance="h6" className="mb-1">
                Review Highlights:
            </Heading>
            <p className="mb-1" data-notranslate>
                {visibleText}
                {showReadFullReviewLink && (
                    <>
                        {' '}
                        <Button
                            appearance="link"
                            onClick={onToggleExpanded}
                            className={styles.summaryToggle}
                            gaTrackingId="304665831973"
                        >
                            Read the Full Review
                        </Button>
                    </>
                )}
            </p>
            <Text size="xs" color="gray">
                AI-generated from the text of visitor reviews
            </Text>
            <div className={styles.summaryActions}>
                {isSubmitted ? (
                    <div className={styles.feedbackThanks}>
                        <Icon iconName="check-circle" />
                        Thank you for your feedback!
                    </div>
                ) : (
                    <>
                        <Button
                            appearance="tertiary-alt"
                            size="xs"
                            iconBeforeElement={<Icon iconName="thumb-up" />}
                            onClick={handleHelpfulClick}
                        >
                            Helpful
                        </Button>
                        <Button
                            appearance="tertiary-alt"
                            size="xs"
                            iconBeforeElement={<Icon iconName="report" />}
                            onClick={handleReportClick}
                        >
                            Report
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

type MobileCoverageRowProps = {
    cellRatings: cellAggregateRating[];
    isMobile: boolean;
    isCampgroundMobile: boolean;
};

// Backend caps cell coverage carriers at 4 per facility, so the carousel only
// ever needs to scroll through up to 4 fixed-width cards. On mobile (≤767px)
// that always overflows, so chevrons stay on. On desktop the row usually fits;
// chevrons appear only if the container is unusually narrow (overflow detected via ResizeObserver).
function MobileCoverageRow({
    cellRatings,
    isMobile,
    isCampgroundMobile,
}: MobileCoverageRowProps) {
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [overflows, setOverflows] = useState(isMobile);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(isMobile);
    const scrollContainerRef = useRef<HTMLUListElement>(null);

    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const isOverflowing = container.scrollWidth > container.clientWidth + 1;
        const showCarousel = isMobile || isOverflowing;
        setOverflows(showCarousel);
        setCanScrollLeft(showCarousel && container.scrollLeft > 0);
        setCanScrollRight(
            showCarousel &&
                container.scrollLeft + container.clientWidth < container.scrollWidth - 1
        );
    }, [isMobile]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return undefined;
        updateScrollState();
        container.addEventListener('scroll', updateScrollState);
        const resizeObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(updateScrollState)
                : null;
        resizeObserver?.observe(container);
        window.addEventListener('resize', updateScrollState);
        return () => {
            container.removeEventListener('scroll', updateScrollState);
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateScrollState);
        };
    }, [updateScrollState, cellRatings.length]);

    const scroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const amount = Math.max(200, container.clientWidth * 0.8);
        container.scrollBy({
            left: direction === 'left' ? -amount : amount,
            behavior: 'smooth',
        });
    };

    return (
        <div>
            <div
                className={cx(styles.coverageHeader, {
                    [styles.isCamping]: isCampgroundMobile,
                })}
            >
                <Heading
                    headingLevel={3}
                    appearance="h5"
                    className={styles.coverageHeading}
                    hasUnderline={isCampgroundMobile ? 'left' : undefined}
                >
                    Mobile Coverage
                </Heading>
                <Button
                    appearance="subtle"
                    isUnstyled
                    className={styles.coverageInfo}
                    onClick={() => setShowInfoModal(true)}
                    screenReaderTextBefore="Show coverage rating descriptions"
                >
                    <Icon iconName="info" width={18} height={18} />
                </Button>
                {overflows && (
                    <div className={styles.coverageChevrons} aria-hidden="true">
                        <ScrollChevron
                            direction="left"
                            isDisabled={!canScrollLeft}
                            onClick={() => scroll('left')}
                        />
                        <ScrollChevron
                            direction="right"
                            isDisabled={!canScrollRight}
                            onClick={() => scroll('right')}
                        />
                    </div>
                )}
            </div>
            <StyledModal
                isOpen={showInfoModal}
                shouldShowCloseButton
                heading="Cell Coverage Strength"
                onRequestClose={() => setShowInfoModal(false)}
            >
                <div className="rec-modal-content-wrap">
                    {[4, 3, 2, 1, 0].map((rating) => (
                        <div key={`coverage-rating-${rating}`}>
                            <strong>{describeCoverage(rating)}</strong>
                            <p>{describeCoverageLong(rating)}</p>
                        </div>
                    ))}
                </div>
            </StyledModal>

            <ul
                ref={scrollContainerRef}
                className={styles.coverageCards}
                role={overflows ? 'region' : undefined}
                aria-label={
                    overflows ? 'Mobile coverage carriers, scrollable' : undefined
                }
                tabIndex={overflows ? 0 : undefined}
            >
                {cellRatings.map((c) => (
                    <li key={c.carrier}>
                        <CoverageCard
                            carrier={c.carrier}
                            rating={c.average_rating}
                            numRatings={c.number_of_ratings}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}

type ScrollChevronProps = {
    direction: 'left' | 'right';
    isDisabled: boolean;
    onClick: () => void;
};

function ScrollChevron({ direction, isDisabled, onClick }: ScrollChevronProps) {
    return (
        <Button
            appearance="subtle"
            isUnstyled
            isDisabled={isDisabled}
            className={cx(styles.chevronButton, {
                [styles.isDisabled]: isDisabled,
            })}
            screenReaderTextBefore={`Scroll mobile coverage ${direction}`}
            onClick={onClick}
            tabIndex={-1}
        >
            <Icon iconName={`chevron-${direction}`} width={24} height={24} />
        </Button>
    );
}

type CoverageCardProps = {
    carrier: string;
    rating: number;
    numRatings: number;
};

function CoverageCard({ carrier, rating, numRatings }: CoverageCardProps) {
    const reviewLabel = `${numRatings} ${numRatings === 1 ? 'review' : 'reviews'}`;
    return (
        <div className={styles.coverageCard}>
            <div className="text-md">
                <strong className="font-weight-semibold">{carrier}</strong>{' '}
                <span className={styles.coverageCardReviews}>({reviewLabel})</span>
            </div>
            <div className={styles.coverageCardRating}>
                <CellCoverageIcon value={rating} size="sm" />
                <div>
                    <span className={styles.coverageCardRatingValue}>
                        {round(rating, 1)}
                    </span>
                    {numRatings ? ` - ${describeCoverage(rating)}` : ' - No Reviews'}
                </div>
            </div>
            <div className={styles.coverageCardReviewsBottom}>{reviewLabel}</div>
        </div>
    );
}

type ReadOnlyRatingBreakdownProps = {
    total: number;
    starCounts: starCounts;
};

const BREAKDOWN_STARS = ['5', '4', '3', '2', '1'] as const;

function ReadOnlyRatingBreakdown({ total, starCounts }: ReadOnlyRatingBreakdownProps) {
    if (!starCounts || !total) {
        return null;
    }

    const numberFormatter = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        maximumFractionDigits: 0,
    });

    return (
        <div className={styles.breakdown} aria-label="Star rating breakdown">
            {BREAKDOWN_STARS.map((star) => {
                const count = starCounts[star as keyof starCounts] ?? 0;
                const percentage = (count / total) * 100;
                return (
                    <div key={star} className={styles.breakdownRow}>
                        <span className="font-weight-semibold text-md">{star} Stars</span>
                        <div className={styles.breakdownBar}>
                            <div
                                className={styles.breakdownBarFill}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                        <span className="color-text-02 text-md text-right">
                            {`${numberFormatter.format(percentage)}%`}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
